import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";

function userToListItem(user: OMSUser): RedoListItem<OMSUser> {
  return { id: user._id, value: user, type: "text", text: user.name };
}

export const UserSelectDropdown = memo(function UserSelectDropdown({
  disabled,
  assignedUser,
  mixedUsers,
  userValues,
  onUserChange,
  label,
}: {
  disabled: boolean;
  assignedUser: OMSUser | null;
  mixedUsers: boolean;
  userValues: readonly OMSUser[];
  onUserChange: (user: OMSUser) => void;
  label?: string;
}) {
  const [searchString, setSearchString] = useState("");

  const uniqueUsers = useMemo(
    () =>
      Array.from(new Map(userValues.map((user) => [user._id, user])).values()),
    [userValues],
  );

  const searcher = useMemo(
    () => new Fuse(uniqueUsers, { keys: ["name"], threshold: 0.3 }),
    [uniqueUsers],
  );

  const options = useMemo(() => {
    if (!searchString.trim()) {
      return uniqueUsers.map(userToListItem);
    }
    return searcher
      .search(searchString)
      .map((result) => userToListItem(result.item));
  }, [uniqueUsers, searcher, searchString]);

  const selectedItem = useMemo(
    () => (assignedUser ? userToListItem(assignedUser) : undefined),
    [assignedUser],
  );

  const onOptionSelected = ({ item }: { item: RedoListItem<OMSUser> }) => {
    onUserChange(item.value);
  };

  return (
    <RedoSingleSelectDropdownInput<OMSUser>
      disabled={disabled}
      label={label}
      options={options}
      optionSelected={onOptionSelected}
      placeholder={mixedUsers ? "{mixed}" : "Assign user"}
      searchPlaceholder="Search users..."
      searchString={searchString}
      selectedItem={selectedItem}
      setSearchString={setSearchString}
      size="xs"
    />
  );
});
